import React, { useState } from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Users, 
  MapPin, 
  Zap,
  Globe,
  Star,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Calendar
} from 'lucide-react';
import { formatCurrency, formatNumber } from '../utils/visualization';
import { MarketplaceCompany } from '../data/marketplace-schemas';

interface CompanyMetricsCardProps {
  company: MarketplaceCompany;
  comparisonData?: {
    avgRevenue: number;
    avgValuation: number;
    avgUsers: number;
  };
  className?: string;
  onDrillDown?: (companyId: string, metric: string) => void;
}

export const CompanyMetricsCard: React.FC<CompanyMetricsCardProps> = ({
  company,
  comparisonData,
  className = '',
  onDrillDown,
}) => {
  const [expanded, setExpanded] = useState(false);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'public': return 'bg-green-100 text-green-800';
      case 'private': return 'bg-blue-100 text-blue-800';
      case 'acquired': return 'bg-purple-100 text-purple-800';
      case 'merged': return 'bg-yellow-100 text-yellow-800';
      case 'defunct': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDensityColor = (density: string) => {
    switch (density) {
      case 'dense': return 'text-green-600';
      case 'moderate': return 'text-yellow-600';
      case 'sparse': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getComparisonTrend = (value: number, avgValue: number) => {
    const diff = ((value - avgValue) / avgValue) * 100;
    return {
      isPositive: diff >= 0,
      percentage: Math.abs(diff),
      icon: diff >= 0 ? TrendingUp : TrendingDown,
      color: diff >= 0 ? 'text-green-600' : 'text-red-600',
    };
  };

  const revenue = company.financial_metrics.revenue.amount;
  const valuation = company.financial_metrics.valuation?.amount || 0;
  const users = company.user_metrics?.active_users?.count || 0;
  const gmv = company.financial_metrics.gmv?.amount || 0;

  const revenueTrend = comparisonData ? getComparisonTrend(revenue, comparisonData.avgRevenue) : null;
  const valuationTrend = comparisonData && valuation ? getComparisonTrend(valuation, comparisonData.avgValuation) : null;
  const usersTrend = comparisonData && users ? getComparisonTrend(users, comparisonData.avgUsers) : null;

  return (
    <div className={`bg-white rounded-lg shadow-lg border hover:shadow-xl transition-shadow ${className}`}>
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-xl font-semibold text-gray-900">{company.name}</h3>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(company.status)}`}>
                {company.status}
              </span>
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                Founded {company.founded_year}
              </div>
              <div className="flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                {company.headquarters}
              </div>
              {company.website && (
                <a 
                  href={company.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                >
                  <ExternalLink className="h-3 w-3" />
                  Website
                </a>
              )}
            </div>
          </div>
        </div>
        <p className="mt-3 text-sm text-gray-700 line-clamp-2">{company.description}</p>
      </div>

      {/* Key Metrics Grid */}
      <div className="p-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {/* Revenue */}
          <div 
            className="bg-blue-50 rounded-lg p-4 cursor-pointer hover:bg-blue-100 transition-colors"
            onClick={() => onDrillDown?.(company.id, 'revenue')}
          >
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="h-5 w-5 text-blue-600" />
              {revenueTrend && (
                <div className={`flex items-center gap-1 ${revenueTrend.color}`}>
                  <revenueTrend.icon className="h-3 w-3" />
                  <span className="text-xs">{revenueTrend.percentage.toFixed(0)}%</span>
                </div>
              )}
            </div>
            <div className="text-2xl font-bold text-blue-900">{formatCurrency(revenue)}</div>
            <div className="text-sm text-blue-700">Revenue ({company.financial_metrics.revenue.year})</div>
          </div>

          {/* Valuation */}
          {valuation > 0 && (
            <div 
              className="bg-green-50 rounded-lg p-4 cursor-pointer hover:bg-green-100 transition-colors"
              onClick={() => onDrillDown?.(company.id, 'valuation')}
            >
              <div className="flex items-center justify-between mb-2">
                <Star className="h-5 w-5 text-green-600" />
                {valuationTrend && (
                  <div className={`flex items-center gap-1 ${valuationTrend.color}`}>
                    <valuationTrend.icon className="h-3 w-3" />
                    <span className="text-xs">{valuationTrend.percentage.toFixed(0)}%</span>
                  </div>
                )}
              </div>
              <div className="text-2xl font-bold text-green-900">{formatCurrency(valuation)}</div>
              <div className="text-sm text-green-700">Valuation</div>
            </div>
          )}

          {/* Users */}
          {users > 0 && (
            <div 
              className="bg-purple-50 rounded-lg p-4 cursor-pointer hover:bg-purple-100 transition-colors"
              onClick={() => onDrillDown?.(company.id, 'users')}
            >
              <div className="flex items-center justify-between mb-2">
                <Users className="h-5 w-5 text-purple-600" />
                {usersTrend && (
                  <div className={`flex items-center gap-1 ${usersTrend.color}`}>
                    <usersTrend.icon className="h-3 w-3" />
                    <span className="text-xs">{usersTrend.percentage.toFixed(0)}%</span>
                  </div>
                )}
              </div>
              <div className="text-2xl font-bold text-purple-900">{formatNumber(users)}</div>
              <div className="text-sm text-purple-700">Active Providers</div>
            </div>
          )}

          {/* GMV */}
          {gmv > 0 && (
            <div 
              className="bg-amber-50 rounded-lg p-4 cursor-pointer hover:bg-amber-100 transition-colors"
              onClick={() => onDrillDown?.(company.id, 'gmv')}
            >
              <div className="flex items-center justify-between mb-2">
                <Zap className="h-5 w-5 text-amber-600" />
              </div>
              <div className="text-2xl font-bold text-amber-900">{formatCurrency(gmv)}</div>
              <div className="text-sm text-amber-700">GMV</div>
            </div>
          )}
        </div>

        {/* Service Categories */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Service Categories</h4>
          <div className="flex flex-wrap gap-2">
            {company.primary_segments.map((segment) => (
              <span 
                key={segment}
                className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-md"
              >
                {segment.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </span>
            ))}
          </div>
        </div>

        {/* Geography Summary */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-900">Geographic Coverage</h4>
            <div className="flex items-center gap-2">
              {company.geographic_coverage.international && (
                <Globe className="h-4 w-4 text-blue-500" />
              )}
              <span className={`text-sm font-medium ${getDensityColor(company.geographic_coverage.coverage_density)}`}>
                {company.geographic_coverage.coverage_density} coverage
              </span>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Cities:</span>
              <span className="ml-2 font-medium">{company.geographic_coverage.total_cities || 'N/A'}</span>
            </div>
            <div>
              <span className="text-gray-600">States:</span>
              <span className="ml-2 font-medium">{company.geographic_coverage.total_states || 'N/A'}</span>
            </div>
            <div>
              <span className="text-gray-600">International:</span>
              <span className="ml-2 font-medium">{company.geographic_coverage.international ? 'Yes' : 'No'}</span>
            </div>
          </div>
        </div>

        {/* Expand/Collapse for Additional Details */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-center gap-2 py-2 text-sm text-gray-600 hover:text-gray-900 border-t border-gray-200"
        >
          {expanded ? 'Show Less' : 'Show More Details'}
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>

        {/* Expanded Details */}
        {expanded && (
          <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
            {/* Technology Features */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Technology</h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">AI Integration:</span>
                  <span className={`font-medium ${company.technology_differentiation.ai_integration.has_ai ? 'text-green-600' : 'text-gray-500'}`}>
                    {company.technology_differentiation.ai_integration.has_ai ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Mobile First:</span>
                  <span className={`font-medium ${company.technology_differentiation.mobile_first ? 'text-green-600' : 'text-gray-500'}`}>
                    {company.technology_differentiation.mobile_first ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Instant Booking:</span>
                  <span className={`font-medium ${company.technology_differentiation.instant_booking ? 'text-green-600' : 'text-gray-500'}`}>
                    {company.technology_differentiation.instant_booking ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Real-time Tracking:</span>
                  <span className={`font-medium ${company.technology_differentiation.real_time_tracking ? 'text-green-600' : 'text-gray-500'}`}>
                    {company.technology_differentiation.real_time_tracking ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>

            {/* Competitive Advantages */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Competitive Advantages</h4>
              <ul className="text-sm text-gray-700 space-y-1">
                {company.competitive_advantages.map((advantage, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <div className="w-1 h-1 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                    {advantage}
                  </li>
                ))}
              </ul>
            </div>

            {/* Challenges */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Current Challenges</h4>
              <ul className="text-sm text-gray-700 space-y-1">
                {company.challenges.map((challenge, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <div className="w-1 h-1 bg-red-500 rounded-full mt-2 flex-shrink-0" />
                    {challenge}
                  </li>
                ))}
              </ul>
            </div>

            {/* Funding Information */}
            {company.funding_history && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">Funding</h4>
                <div className="text-sm text-gray-700">
                  <div className="flex items-center justify-between mb-1">
                    <span>Total Funding:</span>
                    <span className="font-medium">{formatCurrency(company.funding_history.total_funding.amount)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Funding Rounds:</span>
                    <span className="font-medium">{company.funding_history.funding_rounds.length}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Data Quality */}
            <div className="text-xs text-gray-500 pt-2 border-t">
              <div className="flex items-center justify-between">
                <span>Last Updated:</span>
                <span>{company.last_updated}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Revenue Reliability:</span>
                <span className="capitalize">{company.financial_metrics.revenue.reliability}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};